// Tranche AH pass 1: door detail page.
//
// Renders a single hardware door scoped to the active tenant, plus side
// rails:
//   - sync state (controller-side mirror; if available)
//   - related access groups and snapshots
//   - outbox jobs scoped to this door
//   - recent audit
//
// Routes used (no new server routes added):
//   GET /api/v1/hardware/doors/:doorId
//   GET /api/v1/admin/outbox/jobs?resource_type=door&resource_id=:id
//   GET /api/v1/audit?limit=10

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AuditFeed,
  Button,
  DangerousActionButton,
  EntityDetailLayout,
  Icon,
  OutboxJobList,
  Spinner,
  StatusChip,
  type AuditEntry,
  type OutboxJob,
  type StatusState,
} from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import {
  fetchAuditForResource,
  fetchOutboxJobsForResource,
} from '../lib/detailFetchers.js';

interface DoorDetail {
  id: string;
  unifi_door_id: string;
  name?: string;
  label_override?: string | null;
  status?: string;
  hardware_status?: string;
  online?: boolean;
}

export function DoorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [door, setDoor] = useState<DoorDetail | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [outbox, setOutbox] = useState<OutboxJob[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setDoor(null);
    setError(null);
    Promise.all([
      apiClient.get<{ data: DoorDetail }>(`/api/v1/hardware/doors/${id}`),
      fetchAuditForResource('door', id, 10),
      fetchOutboxJobsForResource('door', id, 10),
    ])
      .then(([d, a, o]) => {
        if (cancelled) return;
        setDoor(d.data);
        setAudit(a);
        setOutbox(o);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load door');
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadTick]);

  if (error) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/hardware')}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <Icon name="arrow_back" /> Hardware
        </button>
        <div className="p-6 bg-error-container text-on-error-container rounded-lg" role="alert">
          {error}
        </div>
      </div>
    );
  }
  if (!door) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  // UX-016: this derived a *synchronisation* badge from a *connectivity*
  // reading — `door.online ? 'synced' : 'failed'` — so a door whose online
  // state the controller never reported (the field is optional) showed a red
  // "Sync failed". The portal does not persist a per-door sync state at all,
  // which is a fact the page should state rather than paper over.
  //
  // Two chips, two facts: what we know about the connection, and what we know
  // about synchronisation, which is nothing.
  const connectivity: StatusState =
    door.online === true ? 'applied' : door.online === false ? 'failed' : 'unknown';
  const connectivityLabel =
    door.online === true ? 'Online' : door.online === false ? 'Offline' : 'Connectivity unknown';

  const displayName = door.label_override ?? door.name ?? door.unifi_door_id;
  const safeActions = (
    <>
      <Button
        variant="secondary"
        onClick={() => navigate(`/hardware?focus=${door.unifi_door_id}`)}
      >
        <Icon name="edit" className="text-sm" />
        <span className="ml-1">Rename</span>
      </Button>
      <Link
        to={`/activity?resource_type=door&resource_id=${encodeURIComponent(door.unifi_door_id)}`}
        className="inline-flex items-center px-3 h-10 rounded-md bg-surface-container text-on-surface hover:bg-surface-container-high font-medium text-sm"
      >
        <Icon name="manage_history" className="text-sm mr-1" /> Management history
      </Link>
      <DangerousActionButton
        actionLabel="Unlock…"
        title={`Unlock ${displayName}?`}
        description="One-time unlock. The door relocks automatically based on its configured rules."
        confirmLabel="Unlock"
        testId="door-unlock"
        // UX-013: `key` was minted here, so a failed unlock that the
        // operator retried inside the same dialog sent a different
        // Idempotency-Key and the backend could not collapse it. The dialog
        // now owns the key for the lifetime of one intent.
        onConfirm={async (reason, intentKey) => {
          await apiClient
            .withIdempotencyKey(intentKey)
            .post(`/api/v1/hardware/doors/${door.unifi_door_id}/unlock`, { reason });
          reload();
        }}
      />
    </>
  );

  const dangerousActions = (
    <>
      <DangerousActionButton
        actionLabel="Emergency lockdown…"
        title={`Lockdown ${displayName}?`}
        description="Locks the door and refuses all credentials until cleared. Reason is captured in audit."
        confirmLabel="Engage lockdown"
        testId="door-lockdown"
        onConfirm={async (reason, intentKey) => {
          await apiClient
            .withIdempotencyKey(intentKey)
            .put(
              `/api/v1/hardware/doors/${door.unifi_door_id}/state`,
              { state: 'locked', reason },
            );
          reload();
        }}
      />
      {/* UX-005: this used to be a DangerousActionButton that demanded a
          reason of at least five characters and then called navigate() — no
          request, no audit entry, nothing removed. The in-code TODO was
          accurate (there is no DELETE on hardware/doors/:doorId) but the
          operator was never told; they typed a justification for an action
          that did not happen.

          Door grants are managed on Door Permissions, so this sends them
          there and says so. No revoke endpoint is invented. */}
      <Link
        to="/hardware/claim"
        className="inline-flex h-10 items-center rounded-md border border-outline-variant px-3 font-medium text-sm text-on-surface hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <Icon name="key_off" className="text-sm mr-1" />
        Manage tenant grants
      </Link>
    </>
  );

  return (
    <EntityDetailLayout
      title={displayName}
      subtitle={door.hardware_status ?? connectivityLabel}
      badges={
        <>
          <StatusChip
            state={connectivity}
            label={connectivityLabel}
            detail={
              door.online === undefined
                ? 'The controller did not report whether this door is online.'
                : undefined
            }
          />
          <StatusChip
            state="unknown"
            label="Sync unknown"
            detail="The portal does not store a per-door synchronisation state yet. Check the sync queue for outstanding jobs."
          />
        </>
      }
      backHref="/hardware"
      backLabel="Hardware"
      safeActions={safeActions}
      dangerousActions={dangerousActions}
      related={<DoorRelatedRail doorId={door.unifi_door_id} />}
      outbox={
        <OutboxJobList
          jobs={outbox}
          viewAllHref={`/outbox?resource_type=door&resource_id=${door.unifi_door_id}`}
        />
      }
      audit={<AuditFeed entries={audit} label="Recent activity" />}
    >
      <section
        aria-label="Hardware"
        className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-surface-container-lowest border border-outline-variant rounded-xl p-6"
      >
        <KV k="Name" v={displayName} />
        <KV k="Online" v={door.online === undefined ? 'Not reported' : door.online ? 'Yes' : 'No'} />
        <KV k="Hardware status" v={door.hardware_status ?? '—'} />
        {door.label_override ? <KV k="Label override" v={door.label_override} /> : null}
      </section>
    </EntityDetailLayout>
  );
}

function DoorRelatedRail({ doorId }: { doorId: string }) {
  // TODO(api-gap): no /hardware/doors/:doorId/access-groups endpoint exists;
  // the access-groups list endpoint accepts a door filter only loosely.
  // We surface deep-links so an operator can pivot to the matching
  // list pages; once an enriched endpoint lands we can render counts.
  // UX-014: of the three links here, only the audit one carried parameters the
  // target could read. `?door=` was never read by AccessGroupsPage and
  // `&kind=snapshot` is not a parameter the audit API has — snapshots hang off
  // journey events, not audit rows. Removing a filter that never applied
  // removes nothing the operator had.
  const targets: Array<{ label: string; icon: string; href: string }> = [
    {
      label: 'Access Zones',
      icon: 'lock_open',
      href: '/access-groups',
    },
    {
      label: 'Management changes for this door',
      icon: 'manage_history',
      href: `/activity?resource_type=door&resource_id=${encodeURIComponent(doorId)}`,
    },
    {
      label: 'Access events and snapshots',
      icon: 'photo_camera',
      href: '/journeys',
    },
  ];
  return (
    <section
      aria-label="Related"
      className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-outline-variant">
        <h2 className="font-label-md text-label-md uppercase text-on-surface-variant">Related</h2>
      </div>
      <ul className="divide-y divide-outline-variant">
        {targets.map((t) => (
          <li key={t.label}>
            <Link
              to={t.href}
              className="flex items-center gap-3 px-4 py-3 hover:bg-surface-container-low"
            >
              <Icon name={t.icon} className="text-on-surface-variant" />
              <span className="font-body-md text-body-md text-on-surface flex-1">{t.label}</span>
              <Icon name="chevron_right" className="text-on-surface-variant" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <div className="font-label-md text-label-md uppercase text-on-surface-variant mb-1">{k}</div>
      <div className="font-body-md text-body-md text-on-surface">{v}</div>
    </div>
  );
}
